import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Wand2, Send, KeyRound, Loader2 } from "lucide-react";
import { api, errMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PRESETS, EMPTY_SELECTIONS } from "../data/studio";
import { StudioHeader } from "../components/StudioHeader";
import { PresetChips } from "../components/PresetChips";
import { SongControlsForm } from "../components/SongControlsForm";
import { OutputStudioPanel } from "../components/OutputStudioPanel";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const CODE_KEY = "suno_access_code";

export default function Studio() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isOwner = !!user?.is_owner;

  const [selections, setSelections] = useState(EMPTY_SELECTIONS);
  const [activePresetId, setActivePresetId] = useState(null);
  const [chat, setChat] = useState("");
  const [song, setSong] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("controls");

  const [codeStatus, setCodeStatus] = useState(null); // {valid, allowance_left, ...}
  const [codeInput, setCodeInput] = useState("");
  const [codeDialog, setCodeDialog] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeError, setCodeError] = useState("");

  const validateStoredCode = useCallback(async (code) => {
    try {
      const res = await api.post("/access/validate", { code });
      setCodeStatus(res.data);
    } catch {
      setCodeStatus(null);
      localStorage.removeItem(CODE_KEY);
    }
  }, []);

  useEffect(() => {
    if (isOwner) return;
    const stored = localStorage.getItem(CODE_KEY);
    if (stored) validateStoredCode(stored);
  }, [isOwner, validateStoredCode]);

  const applyPreset = (preset) => {
    if (!preset) {
      setSelections(EMPTY_SELECTIONS);
      setActivePresetId(null);
      setChat("");
      return;
    }
    setSelections({ ...EMPTY_SELECTIONS, ...preset.selections });
    setActivePresetId(preset.id);
    setChat(preset.chatPrefill || "");
    toast.success(`Loaded "${preset.name}" — fully editable`);
  };

  const resetCustom = () => applyPreset(null);

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    setCodeChecking(true); setCodeError("");
    try {
      const res = await api.post("/access/validate", { code: code });
      setCodeStatus(res.data);
      localStorage.setItem(CODE_KEY, code.toUpperCase());
      setCodeDialog(false);
      setCodeInput("");
      toast.success("Access code activated");
    } catch (e) {
      setCodeError(errMessage(e));
    } finally {
      setCodeChecking(false);
    }
  };

  const generate = async (isRevision) => {
    if (!isOwner && !codeStatus?.valid) {
      setCodeDialog(true);
      return;
    }
    setLoading(true); setError(""); setTab("output");
    try {
      const body = {
        selections,
        presetId: activePresetId || "",
        presetName: activePresetId ? PRESETS.find((p) => p.id === activePresetId)?.name : "Custom song",
        chatInstruction: chat,
        previousSong: isRevision ? previous : null,
      };
      if (!isOwner) body.code = localStorage.getItem(CODE_KEY);
      const res = await api.post("/generate", body);
      setSong(res.data);
      setPrevious({
        title: res.data.title, styles: res.data.styles,
        lyrics: res.data.lyrics, exclude: res.data.exclude, note: res.data.note,
      });
      if (res.data.allowance_left !== undefined && res.data.allowance_left !== null) {
        setCodeStatus((s) => (s ? { ...s, allowance_left: res.data.allowance_left } : s));
      }
      toast.success(isRevision ? "Revision ready" : "Song ready");
    } catch (e) {
      const msg = errMessage(e);
      setError(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setCodeStatus(null);
        localStorage.removeItem(CODE_KEY);
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070B14]">
        <Loader2 className="w-6 h-6 animate-spin text-amber-300" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-200">
      <StudioHeader
        codeStatus={codeStatus}
        onOpenCode={() => setCodeDialog(true)}
        onReset={resetCustom}
        isOwner={isOwner}
        ownerName={user?.name}
        onOwnerNav={() => navigate("/owner")}
      />

      {/* Mobile tab switcher */}
      <div className="lg:hidden sticky top-16 z-20 bg-[#070B14]/90 backdrop-blur border-b border-[#1E2C48]">
        <div className="grid grid-cols-2">
          {[
            { id: "controls", label: "Controls & Direction" },
            { id: "output", label: "Suno v6 Output" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`py-3 text-sm font-semibold transition-colors ${
                tab === t.id ? "text-amber-200 border-b-2 border-amber-400" : "text-slate-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 lg:py-7">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT: controls */}
          <section className={`lg:col-span-6 space-y-6 ${tab === "controls" ? "block" : "hidden lg:block"}`}>
            <PresetChips activeId={activePresetId} onSelect={applyPreset} />

            <div className="rounded-2xl border border-[#1E2C48] bg-[#0D1424]/60 p-4 sm:p-5">
              <SongControlsForm selections={selections} onChange={setSelections} />
            </div>

            {/* Chat directions */}
            <div className="rounded-2xl border border-[#1E2C48] bg-[#0D1424]/60 p-4 sm:p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span className="font-label text-amber-400/90">Extra directions (chat)</span>
              </div>
              <Textarea
                data-testid="chat-directions-input"
                value={chat}
                onChange={(e) => setChat(e.target.value)}
                placeholder='e.g. "Make the female enter after verse two" or "Give the ending more energy"'
                className="min-h-[84px] bg-[#0D1424] border-[#1E2C48] text-slate-100 placeholder:text-slate-500 focus-visible:ring-amber-400/40"
              />
              <p className="text-xs text-slate-500">
                Your latest specific instruction takes priority if it conflicts with a selection — the app notes the change.
              </p>
            </div>

            {/* Actions (desktop) */}
            <div className="hidden lg:flex gap-3">
              <Button
                data-testid="generate-song-button"
                onClick={() => generate(false)}
                disabled={loading}
                className="flex-1 h-12 bg-gradient-to-r from-[#E6C66E] to-[#D4AF37] text-[#0B0E14] font-bold hover:opacity-90"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5 mr-2" />}
                Create song & lyrics
              </Button>
              <Button
                data-testid="revise-song-button"
                onClick={() => generate(true)}
                disabled={loading || !previous}
                className="h-12 px-5 bg-[#121B30] border border-[#1E2C48] text-slate-100 hover:bg-[#1a2540] disabled:opacity-40"
              >
                <Send className="w-4 h-4 mr-2" />
                Revise
              </Button>
            </div>
          </section>

          {/* RIGHT: output */}
          <section className={`lg:col-span-6 ${tab === "output" ? "block" : "hidden lg:block"}`}>
            <div className="lg:sticky lg:top-24">
              <OutputStudioPanel song={song} loading={loading} error={error} />
            </div>
          </section>
        </div>
      </main>

      {/* Mobile sticky action bar */}
      <div className="lg:hidden sticky bottom-0 z-20 bg-[#070B14]/95 backdrop-blur border-t border-[#1E2C48] px-4 py-3 flex gap-3">
        <Button
          data-testid="generate-song-button-mobile"
          onClick={() => generate(false)}
          disabled={loading}
          className="flex-1 h-12 bg-gradient-to-r from-[#E6C66E] to-[#D4AF37] text-[#0B0E14] font-bold"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Wand2 className="w-5 h-5 mr-2" /> Create</>}
        </Button>
        <Button
          data-testid="revise-song-button-mobile"
          onClick={() => generate(true)}
          disabled={loading || !previous}
          className="h-12 px-4 bg-[#121B30] border border-[#1E2C48] text-slate-100 disabled:opacity-40"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>

      {/* Access code dialog */}
      <Dialog open={codeDialog} onOpenChange={setCodeDialog}>
        <DialogContent className="bg-[#0D1424] border-[#1E2C48] text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-50">
              <KeyRound className="w-5 h-5 text-amber-300" /> Enter your access code
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Reading the site and demos is free. To generate songs you need an access code issued by the
              owner. Enter your code below — each generation counts against its allowance. Don't have one
              yet? Contact the owner where you found this tool to request access.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="buyer-access-code-input"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyCode()}
            placeholder="SUNO-XXXXXX"
            className="h-11 bg-[#121B30] border-[#1E2C48] text-slate-100 uppercase"
          />
          {codeError && <p data-testid="code-error" className="text-sm text-red-400">{codeError}</p>}
          <DialogFooter>
            <Button
              data-testid="buyer-access-code-apply"
              onClick={applyCode}
              disabled={codeChecking}
              className="bg-gradient-to-r from-[#E6C66E] to-[#D4AF37] text-[#0B0E14] font-bold"
            >
              {codeChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
